import { useFetcher, useLocation, useNavigate, useOutletContext } from '@remix-run/react';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ManageAction from '~/components/Actions/ManageAction';
import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import MarkDownHelperText from '~/components/Markdown/HelperText';
import SlideUpModal from '~/components/Modal/SlideUpModal';
import Text from '~/components/Text';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { Subscription } from '~/interfaces/subscriptions';

import useToast from '~/hooks/useToast';

interface TextAreaProps {
  postId: string;
  commentId?: string;
  subscription?: Subscription;
  userId?: string;
  text: string;
  buttonType?: 'submit' | 'button';
  onClick?: () => void;
  onChange?: (e: any) => void;
  defaultValue?: string;
  disabled?: boolean;
  rows?: number;
}

const ALLOWED_CHARACTERS = 1000;

const TextArea = ({
  postId,
  commentId,
  subscription,
  text,
  buttonType,
  onClick,
  onChange,
  defaultValue,
  userId = '',
  disabled = false,
  rows = 5,
}: TextAreaProps) => {
  const { t } = useTranslation();

  const [content, setContent] = useState<string>('');
  const charCount = content.length || 0;
  const charactersRemaining = ALLOWED_CHARACTERS - charCount;
  const isOverLimit = charactersRemaining < 0;
  const idKey = `comment_${subscription?.id}_${commentId}`;

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const textAreaComment = textAreaRef.current;

    const onInput = (event: Event) => {
      const textarea = event.target as HTMLTextAreaElement;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    if (textAreaComment) {
      textAreaComment.style.height = 'auto';
      textAreaComment.style.height = `${textAreaComment.scrollHeight}px`;
      textAreaComment.style.overflowY = 'hidden';
      textAreaComment.addEventListener('input', onInput, false);
    }

    return () => {
      // Cleanup event listener if needed
      if (textAreaComment) {
        textAreaComment.removeEventListener('input', onInput, false);
      }
    };
  }, [textAreaRef]);

  useEffect(() => {
    const textAreaComment = textAreaRef.current;

    if (disabled && textAreaComment) {
      textAreaComment.style.height = 'auto';
    }
  }, [disabled]);

  return (
    <div className="relative">
      <div className="absolute right-2 top-1 z-20">
        <MarkDownHelperText />
      </div>
      <div
        className={cx(
          'relative overflow-hidden rounded-wt border border-gray-200 pb-10 shadow-sm focus-within:ring-1',
          isOverLimit
            ? 'focus-within:border-red-500 focus-within:ring-red-500'
            : 'focus-within:border-wt-primary focus-within:ring-wt-primary',
        )}
      >
        <label htmlFor={idKey} className="sr-only">
          {t('comments.commenting.comment_action_placeholder')}
        </label>
        <textarea
          rows={rows}
          ref={textAreaRef}
          name="content"
          id={idKey}
          className="block w-full resize-none border-0 bg-transparent py-3 !text-sm focus:ring-0"
          placeholder={`${t('comments.commenting.comment_action_placeholder')}...`}
          required
          onChange={(e) => {
            setContent(e.target.value);
            onChange?.(e);
          }}
          defaultValue={defaultValue}
        />
        <input type="hidden" name="post_id" value={postId} />
        <input type="hidden" name="comment_id" value={commentId} />
        <input type="hidden" name="subscription_id" value={subscription?.id} />
        <input type="hidden" name="user_id" value={userId} />
        <StatusInputs name="characters" errorMessage={t('comments.commenting.characters_error')} successMessage="" />
        <StatusInputs name="comment" errorMessage={t('errors.default')} successMessage="" />
        <div className="absolute bottom-2 left-2 flex items-center justify-between pt-2">
          <Text
            as="p"
            size="xs"
            theme="content"
            weight="medium"
            className={cx('flex items-center !text-xs', isOverLimit ? 'opacity-100' : 'opacity-40')}
          >
            <span className={cx(isOverLimit && 'text-red-500')}>{charactersRemaining}</span>
          </Text>
        </div>
        <div className="absolute bottom-2 right-2">
          <Button
            btnSize="sm"
            type={buttonType}
            onClick={() => {
              setContent('');
              onClick?.();
            }}
            disabled={disabled || isOverLimit}
          >
            {text}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface Props {
  postId: string;
  commentId?: string;
  subscription?: Subscription;
  onCommentCreated?: (newComment: any) => void;
  rows?: number;
  isPremiumCommentsOnly?: boolean;
}

const CommentAction = ({
  postId,
  commentId,
  onCommentCreated,
  subscription,
  rows,
  isPremiumCommentsOnly = false,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { admin } = useOutletContext<OutletContext>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldOptimiallySubmit, setShouldOptimiallySubmit] = useState(false);
  const [mockModalText, setMockModalText] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const fetcher = useFetcher();
  const isSubmitting = fetcher.state === 'submitting';
  const fetcherData = fetcher.data as any;
  const error = fetcherData?.toast?.status === 'error';
  useToast(fetcherData?.toast);

  const isAdmin = Boolean(admin);
  const isReplying = Boolean(commentId);
  const ctaText = isReplying ? t('comments.replying.reply') : t('comments.commenting.post');
  const ctaSubmittingText = isReplying
    ? `${t('comments.replying.replying')}...`
    : `${t('comments.commenting.posting')}...`;

  const shouldRenderPremiumPopup = subscription?.tier !== 'premium' && isPremiumCommentsOnly && !isAdmin;
  const needsToSetProfileDetails = !subscription?.comment_details_set && !isAdmin;

  const handleOptimalSubmit = () => {
    if (shouldOptimiallySubmit) {
      formRef.current?.submit();
      setShouldOptimiallySubmit(false);
    }
  };

  // When users first add their name and details we want to optimally submit the form for them upon completion.
  // This call back will check to see if there is a comment to be posted and submit when the form has been rendered to page.
  // It will then clear the states so it does not happen by accident again
  useEffect(() => {
    if (formRef.current && mockModalText && shouldOptimiallySubmit) {
      formRef.current.getElementsByTagName('button')[0]?.click();
      setShouldOptimiallySubmit(false);
      setMockModalText('');
    }
  }, [formRef.current, shouldOptimiallySubmit, handleOptimalSubmit]);

  useEffect(() => {
    if (fetcherData && !error && onCommentCreated) {
      onCommentCreated(fetcherData);
      formRef.current?.reset();
      setMockModalText('');
    }
  }, [fetcherData, error]);

  // Prompt a user to upgrade to premium if they are just a free user and the comments are premium only
  if (shouldRenderPremiumPopup) {
    return (
      <>
        <SlideUpModal
          title={`${t('comments.upgrade.title')}`}
          description={
            <div>
              <p>{t('comments.upgrade.description')}</p>
            </div>
          }
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <Button
            type="button"
            variant="primary"
            className="flex w-full justify-center"
            isLoading={isSubmitting}
            onClick={() => navigate(`/upgrade?post_url=${location.pathname}`)}
          >
            {t('comments.upgrade.cta')}
          </Button>
        </SlideUpModal>
        <TextArea
          postId={postId}
          commentId={commentId}
          subscription={subscription}
          text={isSubmitting ? ctaSubmittingText : ctaText}
          buttonType="button"
          onClick={() => setIsModalOpen(true)}
          onChange={(e) => setMockModalText(e.target.value)}
        />
      </>
    );
  }

  // Prompt user for their profile details if this is the first time they are commenting and don't have a a profile set.
  // Without this information we would have nothing to display on screen as we obvisouly should never be displaying user email information publicly.
  if (needsToSetProfileDetails) {
    return (
      <>
        <SlideUpModal
          title={t('comments.commenting.profile_title') || ''}
          description={
            <div>
              <p>{t('comments.commenting.profile_description')}</p>
              <p className="pt-1 text-xs font-medium text-gray-500">
                ({t('comments.commenting.profile_gravatar')}{' '}
                <a
                  className="text-wt-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://support.beehiiv.com/hc/en-us/articles/13111175166999"
                >
                  Gravatar.
                </a>
                )
              </p>
            </div>
          }
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <ManageAction onSuccess={() => setShouldOptimiallySubmit(true)} />
        </SlideUpModal>
        <TextArea
          postId={postId}
          commentId={commentId}
          subscription={subscription}
          text={isSubmitting ? ctaSubmittingText : ctaText}
          buttonType="button"
          onClick={() => setIsModalOpen(true)}
          onChange={(e) => setMockModalText(e.target.value)}
        />
      </>
    );
  }

  return (
    <div className="flex items-start">
      <div className="min-w-0 flex-1">
        <fetcher.Form
          ref={formRef}
          method="post"
          action={isAdmin ? '/admins/comments' : '/comments'}
          className="relative"
        >
          <TextArea
            postId={postId}
            commentId={commentId}
            subscription={subscription}
            userId={admin?.id || ''}
            text={isSubmitting ? ctaSubmittingText : ctaText}
            defaultValue={mockModalText}
            rows={rows}
            buttonType="submit"
            disabled={isSubmitting}
          />
        </fetcher.Form>
      </div>
    </div>
  );
};

export default CommentAction;
